/**
 * <product-carousel> custom element.
 *
 * Wraps a Swiper instance. Until Swiper has loaded, the section's CSS shows a native
 * scroll-snap carousel and this element wires the arrows to it (see #enableFallbackNav). Markup contract (see sections/article-product-carousel.liquid):
 *   <product-carousel data-swiper-src data-slides-{mobile,tablet,desktop} data-space-{mobile,tablet,desktop}>
 *     <button data-carousel-prev> … <div class="swiper" data-carousel-swiper> … </div>
 *     <button data-carousel-next> … <div data-carousel-scrollbar></div>
 *   </product-carousel>
 */

/**
 * Loads the vendored Swiper 11 build once per page.
 *
 * The theme already ships Swiper 3 as window.Swiper, so the bundle is evaluated in a
 * function scope: this keeps Swiper 11 private and leaves the theme's global untouched.
 */
class SwiperLoader {
  static #promise = null;

  static load(src) {
    if (!SwiperLoader.#promise) {
      SwiperLoader.#promise = fetch(src)
        .then((response) => {
          if (!response.ok) throw new Error(`Failed to load ${src}`);
          return response.text();
        })
        .then((code) => new Function(`${code}\nreturn Swiper;`)())
        .catch((error) => {
          SwiperLoader.#promise = null;
          throw error;
        });
    }
    return SwiperLoader.#promise;
  }
}

class ProductCarousel extends HTMLElement {
  static TABLET_BREAKPOINT = 768;
  static DESKTOP_BREAKPOINT = 1024;

  #swiper = null;
  #disconnected = false;
  #fallbackNav = null;

  connectedCallback() {
    this.#disconnected = false;
    this.#enableFallbackNav();
    SwiperLoader.load(this.dataset.swiperSrc)
      .then((Swiper) => {
        if (this.#disconnected) return;
        this.#disableFallbackNav();
        this.#swiper = new Swiper(this.#swiperEl, this.#buildOptions());
      })
      .catch((error) => console.error('[product-carousel]', error));
  }

  disconnectedCallback() {
    this.#disconnected = true;
    this.#disableFallbackNav();
    this.#destroy();
  }

  /** Makes the arrows scroll the native fallback carousel until Swiper takes over. */
  #enableFallbackNav() {
    const track = this.querySelector('.swiper-wrapper');
    this.#fallbackNav = new AbortController();
    const { signal } = this.#fallbackNav;
    const scroll = (direction) => {
      const slide = track.querySelector('.swiper-slide');
      if (!slide) return;
      const step = slide.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 0);
      track.scrollBy({ left: direction * step, behavior: 'smooth' });
    };
    this.querySelector('[data-carousel-prev]')?.addEventListener('click', () => scroll(-1), { signal });
    this.querySelector('[data-carousel-next]')?.addEventListener('click', () => scroll(1), { signal });
  }

  #disableFallbackNav() {
    this.#fallbackNav?.abort();
    this.#fallbackNav = null;
  }

  get #swiperEl() {
    return this.querySelector('[data-carousel-swiper]');
  }

  #number(name, fallback) {
    const value = parseFloat(this.dataset[name]);
    return Number.isNaN(value) ? fallback : value;
  }

  #buildOptions() {
    return {
      slidesPerView: this.#number('slidesMobile', 2),
      spaceBetween: this.#number('spaceMobile', 16),
      watchOverflow: true,
      navigation: {
        prevEl: this.querySelector('[data-carousel-prev]'),
        nextEl: this.querySelector('[data-carousel-next]'),
      },
      scrollbar: {
        el: this.querySelector('[data-carousel-scrollbar]'),
        draggable: true,
      },
      breakpoints: {
        [ProductCarousel.TABLET_BREAKPOINT]: {
          slidesPerView: this.#number('slidesTablet', 3),
          spaceBetween: this.#number('spaceTablet', 24),
        },
        [ProductCarousel.DESKTOP_BREAKPOINT]: {
          slidesPerView: this.#number('slidesDesktop', 4),
          spaceBetween: this.#number('spaceDesktop', 24),
        },
      },
    };
  }

  #destroy() {
    if (!this.#swiper) return;
    this.#swiper.destroy(true, true);
    this.#swiper = null;
  }
}

if (!customElements.get('product-carousel')) {
  customElements.define('product-carousel', ProductCarousel);
}
